import React, { useMemo } from "react";
import { Tables } from "@/lib/supabase/supabaseTypesImport";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ChatPreviewList } from "./chat-preview-list.component";
import { UnreadFilterToggle } from "./unread-filter-toggle.component";
import { cn } from "../../lib/utils/cn";
import { ChannelType } from "@/lib/store/Realtime/message.store";

export type ChatSidebarTab = "all" | "jobs" | "general" | "clients_only" | "vendors_only" | "job_requests" | "job_request_denied";

interface ChatSidebarProps {
	channels: Tables<"message_channels">[];
	selectedChannelId?: string;
	onSelectChannel: (channel: Tables<"message_channels">) => void;
	organizationId: string;
	userId: string;
	className?: string;
	defaultTab?: ChatSidebarTab;
	hideTabs?: Omit<ChatSidebarTab, "all">[];
	orgIdToClientNameMap?: Record<string, string>;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
	channels,
	selectedChannelId,
	onSelectChannel,
	organizationId,
	userId,
	className,
	defaultTab = "all",
	hideTabs = [],
	orgIdToClientNameMap,
}) => {
	const hideVendorTab = hideTabs.includes("vendors_only");
	const hideClientTab = hideTabs.includes("clients_only");
	const hideJobTab = hideTabs.includes("jobs");
	const hideGeneralTab = hideTabs.includes("general");
	const hideJobRequestTab = hideTabs.includes("job_requests");
	const hideJobRequestDeniedTab = hideTabs.includes("job_request_denied");

	const channelsByType = useMemo(() => {
		const vendorChannels = hideVendorTab ? [] : channels.filter(
			(channel) => channel.customer_org_id === organizationId
		);
		const clientChannels = hideClientTab ? [] : channels.filter(
			(channel) => channel.vendor_org_id === organizationId
		);
		const jobChannels = hideJobTab ? [] : channels.filter(
			(channel) => channel.channel_type === ChannelType.JOB && channel.vendor_org_id === organizationId
		);
		const generalChannels = hideGeneralTab ? [] : channels.filter(
			(channel) => channel.channel_type === ChannelType.GENERAL
		);
		const jobRequestChannels = hideJobRequestTab ? [] : channels.filter(
			(channel) => channel.channel_type === ChannelType.JOB_REQUEST
		);
		const jobRequestDeniedChannels = hideJobRequestDeniedTab ? [] : channels.filter(
			(channel) => channel.channel_type === ChannelType.JOB_REQUEST_DENIED
		);
		return {
			all: channels,
			vendors: vendorChannels,
			clients: clientChannels,
			jobs: jobChannels,
			general: generalChannels,
			jobRequests: jobRequestChannels,
			jobRequestDenied: jobRequestDeniedChannels,
		};
	}, [channels, organizationId]);

	return (
		<div className={cn("flex flex-col h-full bg-gray-50 border-r border-gray-200", className)}>
			<Tabs defaultValue={defaultTab} className="flex flex-col h-full">
				<div className="flex flex-col border-b border-gray-200">
					<div className="px-4 py-2 flex justify-between items-center">
						<div className="text-sm font-medium text-gray-600">Chats</div>
						<UnreadFilterToggle />
					</div>
					<TabsList className="flex flex-wrap gap-2 w-full bg-transparent p-2 h-auto justify-start">
						<TabsTrigger
							value="all"
							className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-none"
						>
							Alle
						</TabsTrigger>
						{!hideJobTab && (
							<TabsTrigger
								value="jobs"
								className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-none"
							>
								Auftragsbezogen
							</TabsTrigger>
						)}
						{!hideGeneralTab && (
							<TabsTrigger
								value="general"
								className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-none"
							>
								Allgemein
							</TabsTrigger>
						)}
						{!hideClientTab && (
							<TabsTrigger
								value="clients_only"
								className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-none"
							>
								Kunden
							</TabsTrigger>
						)}
						{!hideVendorTab && (
							<TabsTrigger
								value="vendors_only"
								className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-none"
							>
								Fremdlabore
							</TabsTrigger>
						)}
						{!hideJobRequestTab && (
							<TabsTrigger
								value="job_requests"
								className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-none"
							>
								Auftragseingang
							</TabsTrigger>
						)}
						{!hideJobRequestDeniedTab && (
							<TabsTrigger
								value="job_request_denied"
								className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-none"
							>
								Abgelehnte Aufträge
							</TabsTrigger>
						)}
					</TabsList>
				</div>
				<div className="flex-1 overflow-y-auto">
					<TabsContent value="all" className="mt-0 h-full">
						<ChatPreviewList
							channels={channelsByType.all}
							selectedChannelId={selectedChannelId}
							onSelectChannel={onSelectChannel}
							userId={userId}
							organizationId={organizationId}
							orgIdToClientNameMap={orgIdToClientNameMap}
						/>
					</TabsContent>

					{!hideClientTab && (
						<TabsContent value="clients_only" className="mt-0 h-full">
							<ChatPreviewList
								channels={channelsByType.clients}
								selectedChannelId={selectedChannelId}
								onSelectChannel={onSelectChannel}
								userId={userId}
								organizationId={organizationId}
								orgIdToClientNameMap={orgIdToClientNameMap}
							/>
						</TabsContent>
					)}

					{!hideVendorTab && (
						<TabsContent value="vendors_only" className="mt-0 h-full">
							<ChatPreviewList
								channels={channelsByType.vendors}
								selectedChannelId={selectedChannelId}
								onSelectChannel={onSelectChannel}
								userId={userId}
								organizationId={organizationId}
								orgIdToClientNameMap={orgIdToClientNameMap}
							/>
						</TabsContent>
					)}

					{!hideJobTab && (
						<TabsContent value="jobs" className="mt-0 h-full">
							<ChatPreviewList
								channels={channelsByType.jobs}
								selectedChannelId={selectedChannelId}
								onSelectChannel={onSelectChannel}
								userId={userId}
								organizationId={organizationId}
								orgIdToClientNameMap={orgIdToClientNameMap}
							/>
						</TabsContent>
					)}

					{!hideGeneralTab && (
						<TabsContent value="general" className="mt-0 h-full">
							<ChatPreviewList
								channels={channelsByType.general}
								selectedChannelId={selectedChannelId}
								onSelectChannel={onSelectChannel}
								userId={userId}
								organizationId={organizationId}
								orgIdToClientNameMap={orgIdToClientNameMap}
							/>
						</TabsContent>
					)}

					{!hideJobRequestTab && (
						<TabsContent value="job_requests" className="mt-0 h-full">
							<ChatPreviewList
								channels={channelsByType.jobRequests}
								selectedChannelId={selectedChannelId}
								onSelectChannel={onSelectChannel}
								userId={userId}
								organizationId={organizationId}
								orgIdToClientNameMap={orgIdToClientNameMap}
							/>
						</TabsContent>
					)}

					{!hideJobRequestDeniedTab && (
						<TabsContent value="job_request_denied" className="mt-0 h-full">
							<ChatPreviewList
								channels={channelsByType.jobRequestDenied}
								selectedChannelId={selectedChannelId}
								onSelectChannel={onSelectChannel}
								userId={userId}
								organizationId={organizationId}
								orgIdToClientNameMap={orgIdToClientNameMap}
							/>
						</TabsContent>
					)}
				</div>
			</Tabs>
		</div>
	);
};